<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link
          class="text-decoration--none"
          :to="{ name: 'reportCategories' }"
          >Report Reasons</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="my-6">
        <v-form ref="form" class="report-category-form">
          <label class="text-field-label">Label</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.label"
            :error-messages="form.$getError('label')"
            :loading="loading"
          ></v-text-field>

          <div class="d-flex mt-4">
            <v-btn
              v-if="form.id"
              color="error"
              class="mr-4 px-6"
              height="40px"
              :loading="form.$busy"
              @click="deleteDialog = true"
              >Delete</v-btn
            >

            <v-spacer></v-spacer>

            <v-btn
              color="primary"
              class="px-6"
              height="40px"
              @click="
                reportCategory
                  ? updateReportCategoryDetails()
                  : addReportCategory()
              "
              :loading="form.$busy"
              >Save</v-btn
            >
          </div>
        </v-form>

        <v-dialog v-model="deleteDialog" max-width="290">
          <v-card>
            <v-card-title class="headline">Confirm Delete</v-card-title>

            <v-card-text>Are you sure you want to delete this?</v-card-text>

            <v-card-actions>
              <v-btn text @click="deleteDialog = false" :loading="form.$busy"
                >Cancel</v-btn
              >

              <v-spacer></v-spacer>

              <v-btn
                color="red"
                text
                @click="removeReportCategory"
                :loading="form.$busy"
                >Yes, Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'

import Form from '@/utils/form'

export default {
  name: 'ReportCategoryDetails',

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline,
      },
      loading: false,
      deleteDialog: false,
      form: new Form({
        label: '',
      }),
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      showModal: false,
    }
  },

  computed: {
    ...mapState({
      reportCategory: (state) => state.reportCategory.reportCategoryDetails,
      authUser: (state) => state.auth.user,
    }),
    displayName() {
      return this.form.label
    },
  },

  methods: {
    ...mapActions({
      getReportCategoryDetails: 'reportCategory/getReportCategoryDetails',
      createReportCategory: 'reportCategory/createReportCategory',
      updateReportCategory: 'reportCategory/updateReportCategory',
      deleteReportCategory: 'reportCategory/deleteReportCategory',
    }),

    ...mapMutations({
      clearReportCategoryDetails: 'reportCategory/clearReportCategoryDetails',
    }),

    async getReportCategory() {
      this.loading = true
      await this.getReportCategoryDetails(this.$route.params.id)
      this.form = new Form(this.reportCategory)
      this.loading = false
    },

    async updateReportCategoryDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.updateReportCategory(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar(
            'Report category details successfully updated!',
            'success'
          )
          this.$router.replace({ name: 'reportCategories' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async addReportCategory() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.createReportCategory(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('Report category successfully added!', 'success')
          this.$router.replace({ name: 'reportCategories' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    removeReportCategory() {
      this.form.$busy = true
      this.deleteReportCategory(this.form.id)
        .then(() => {
          this.form.$busy = false
          this.deleteDialog = false
          this.showSnackbar('Report category deleted successfully!')
          this.$router.replace('/report-categories')
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 404) {
            this.deleteDialog = false
            this.showSnackbar(
              'The report category you are trying to delete is no longer existing.',
              'error'
            )
          }
        })
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },

  created() {
    if (this.$router.currentRoute.name === 'reportCategory.details') {
      this.getReportCategory()
    }
  },

  destroyed() {
    this.clearReportCategoryDetails()
  },

  watch: {
    $route() {
      this.getReportCategory()
    },
  },
}
</script>

<style lang="scss" scoped>
.report-category-form {
  width: 400px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
